<template>
  <div class="pspv-Wrapper">
    <OkrElementsEmpty v-if="isNoElements" hide-hero>
      {{ t('filter.no_objective') }}
    </OkrElementsEmpty>
    <template v-else>
      <div class="pspv-Actions">
        <slot name="before-actions" />

        <div class="pspv-Action_Nav">
          <ProgressViewColumnsSelect
            :count="progressViewState.columnCount"
            :disabled="progressViewState.isLoading"
            :options="METRICS_CARD_PROGRESS_VIEW_COLUMN_COUNT_OPTIONS"
            class="pspv-Action_ColumnsSelect"
            @update-count="onUpdateColumnCount"
          />
          <AppDatesNavigation
            :disabled="progressViewState.isLoading"
            :is-reset-disabled="isResetButtonDisabled"
            @move-to-startpoint="moveToStartpoint"
            @move-backward="moveBackward"
            @move-forward="moveForward"
          />
          <AppRadioGroup
            :disabled="progressViewState.isLoading"
            :model-value="progressViewState.granulationId"
            :options="getPeriodGranulationOptions"
            name="pspv-view-switch"
            @update:model-value="onGranulationChange"
          />
        </div>
      </div>

      <div class="pspv-Content">
        <div class="pspv-Elements">
          <AppTable
            :columns="progressViewColumns"
            :data="progressViewRows"
            :disable-user-select="false"
            :loading="progressViewState.isLoading && !progressViewState.isSoftLoading"
            border-on-last-row
            no-border
            type="primary-next"
          >
            <template #header-cells>
              <div class="pspv-HeadNameCell">
                {{ t('homepage.key_result_qty', 0) }}
              </div>
            </template>

            <template #cell="{ columnKey, item }">
              <div
                v-if="columnKey === TABLE_COLUMNS_KEYS.NAME"
                :style="{ '--ob-pspv-row-depth': item.depth }"
                class="pspv-NameCell"
              >
                <div class="pspv-NameCell_Expand">
                  <AppButton
                    v-if="isExpandable(item)"
                    :class="{
                      'pspv-ExpandButton-expanded': progressViewState.expandedItems.includes(
                        item.id
                      )
                    }"
                    :size="BUTTON_SIZES.SM"
                    class="pspv-ExpandButton"
                    remove-padding
                    type="ghost-next"
                    @click="toggleExpand(item.id)"
                  >
                    <AppIcon height="24" icon-name="okr-table-expand" width="24" />
                  </AppButton>
                </div>

                <OkrIcon :objective="item" />

                <TitleItems
                  :objective="item"
                  :semi-transparent-id="!item.fitFilterCriteria"
                  :show-element-id="false"
                  name-as-link
                  with-tooltip
                  @click-on-name="
                    $emit('edit-element', { view: OKR_TYPE_TO_FORM_VIEW[item.typeId], item })
                  "
                />
              </div>

              <OkrElementOwner
                v-if="columnKey === TABLE_COLUMNS_KEYS.OWNER"
                :okr-element="item"
                show-avatar-replacer
              />

              <CheckinCell
                v-if="columnKey === TABLE_COLUMNS_KEYS.CHECKIN"
                :item="item"
                compact
                @open-checkin-modal="$emit('open-checkin-modal', $event)"
              />
            </template>

            <template #loading>
              <PersonalStatisticsProgressViewTableLoader />
            </template>
          </AppTable>

          <div
            v-if="progressViewState.showLoadMoreButton && !progressViewState.isLoading"
            class="pspv-LoadMore"
          >
            <AppLoadMoreButton @click="loadMore" />
          </div>
        </div>

        <div :class="{ 'pspv-Grid-reloading': progressViewState.isSoftLoading }" class="pspv-Grid">
          <SkeletonItem
            v-if="progressViewState.isSoftLoading"
            animation-opacity="0.8"
            class="pspv-Grid_Loader"
            color="transparent"
            height="100%"
            width="100%"
          />
          <div :style="{ minWidth }" class="pspv-Grid_Content">
            <ProgressModuleGridHeader :periods="periodsForDisplay" />
            <ProgressModuleGridRow
              v-for="element in progressViewRows"
              :key="element.uid"
              :element
              :placement="PROGRESS_MODULE_CELL_PLACEMENTS.PROGRESS_VIEW"
            />
            <ProgressModuleGridFooter
              v-if="!progressViewState.isLoading || progressViewState.isSoftLoading"
              :periods="periodsForDisplay"
            />
            <PersonalStatisticsProgressViewGridLoader
              v-if="progressViewState.isLoading && !progressViewState.isSoftLoading"
              :column-count="progressViewState.columnCount"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { isEmpty, isNull, xor } from 'lodash'
import { computed, inject, onMounted, provide, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import ObjectivesApiHandler, { SEARCH_TYPES } from '@/api/okr-elements'
import { BUTTON_SIZES } from '@/utils/components-configurations/app-button'
import {
  PROGRESS_MODULE_CELL_PLACEMENTS,
  PROGRESS_MODULE_VIEW_GRANULATION
} from '@/utils/custom-dashboard-helper'
import { usePeriodGranulation, useViewPointLastDate } from '@/utils/element-progress'
import { ELEMENT_PROGRESS_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { DEFAULT_GRADE_TYPES, getAvailableCustomRangeParams } from '@/utils/homepage'
import {
  METRICS_CARD_PROGRESS_VIEW_COLUMN_COUNT_OPTIONS,
  METRICS_CARD_PROGRESS_VIEW_DEFAULT_COLUMN_COUNT
} from '@/utils/homepage-progres-view'
import {
  currentModuleStateInjectionKey,
  homePageStateInjectionKey,
  progressViewStateInjectionKey
} from '@/utils/injection-keys'
import { UNSELECTED_INTERVAL } from '@/utils/interval'
import { OKR_TYPE_TO_FORM_VIEW } from '@/utils/objectives'
import {
  isTimeRangeSelectedInsteadOfInterval,
  NEW_HP_FILTERS_KEYS
} from '@/utils/okr-elements/filters'
import { useProgressViewPeriods } from '@/utils/progress-view'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'
import {
  DEFAULT_USERS_SETTINGS,
  HOMEPAGE_METRICS_WITHOUT_KPI_CARD_CONFIG,
  USER_SETTINGS_MAPPER
} from '@/utils/user-settings'

import ProgressModuleGridFooter from '@/components/dashboard/custom-dashboard/modules/progress-module/ProgressModuleGridFooter'
import ProgressModuleGridHeader from '@/components/dashboard/custom-dashboard/modules/progress-module/ProgressModuleGridHeader'
import ProgressModuleGridRow from '@/components/dashboard/custom-dashboard/modules/progress-module/ProgressModuleGridRow'
import OkrElementsEmpty from '@/components/home-page/OkrElementsEmpty'
import OkrIcon from '@/components/objectives/items/OkrIcon'
import TitleItems from '@/components/objectives/items/TitleItems'
import OkrElementOwner from '@/components/objectives/okr-elements-list/OkrElementOwner'
import ProgressViewColumnsSelect from '@/components/objectives/progress-view/ProgressViewColumnsSelect'
import CheckinCell from '@/components/objectives/table/cells/CheckinCell'
import PersonalStatisticsProgressViewGridLoader from '@/components/personal-statistics/PersonalStatisticsProgressViewGridLoader'
import PersonalStatisticsProgressViewTableLoader from '@/components/personal-statistics/PersonalStatisticsProgressViewTableLoader'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDatesNavigation from '@/components/ui/AppDatesNavigation/AppDatesNavigation'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppLoadMoreButton from '@/components/ui/AppLoadMoreButton/AppLoadMoreButton'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'
import AppTable from '@/components/ui/Table/AppTable'

defineOptions({
  name: 'PersonalStatisticsProgressView'
})

const props = defineProps({
  selectedIntervals: {
    type: Array,
    required: true
  },

  selectedDates: {
    type: Object,
    required: true
  },

  selectedUserOrGroup: {
    type: [String, Number],
    required: true
  },

  userSelected: {
    type: Boolean
  },

  predefinedWorkspaceId: {
    type: Number,
    default: null
  }
})

const { t } = useI18n()

const emit = defineEmits({
  'edit-element': null,
  'open-checkin-modal': null,
  'update-okr-elements': null,
  'empty-change': null
})

//#region Saved config
const store = useStore()

const metricsCardConfig = computed(
  () => store.getters['system/homepageMetricsWithoutKpiCardConfig']
)

const { granulationId: savedGranulationId, columnCount: savedColumnCount } = metricsCardConfig.value

const initialGranulationId = Object.values(PROGRESS_MODULE_VIEW_GRANULATION).some(
  granulation => granulation.value === savedGranulationId
)
  ? savedGranulationId
  : DEFAULT_USERS_SETTINGS[HOMEPAGE_METRICS_WITHOUT_KPI_CARD_CONFIG].granulationId

const initialColumnCount = METRICS_CARD_PROGRESS_VIEW_COLUMN_COUNT_OPTIONS.includes(
  savedColumnCount
)
  ? savedColumnCount
  : METRICS_CARD_PROGRESS_VIEW_DEFAULT_COLUMN_COUNT
//#endregion

//#region Progress view state
const DEFAULT_LIMIT = 10

const progressViewState = reactive({
  elements: [],
  periods: [],
  granulationId: initialGranulationId,
  columnCount: initialColumnCount,
  requestParameters: {
    /** @type {number | null} */
    viewPointLastDate: null,
    granulationId: initialGranulationId,
    columnCount: initialColumnCount,
    shiftSize: 0
  },
  expandedItems: [],
  isLoading: false,
  isSoftLoading: false,
  offset: 0,
  limit: DEFAULT_LIMIT,
  showLoadMoreButton: false
})

const isNoElements = computed(() => {
  return isEmpty(progressViewState.elements) && !progressViewState.isLoading
})
//#endregion

//#region Rows and columns
const toggleExpand = id => {
  progressViewState.expandedItems = xor(progressViewState.expandedItems, [id])
}

const isExpandable = element => {
  return !isEmpty(element.childElements) || element.childCount > 0
}

const flattenElements = (elements, depth = 0) => {
  return elements.reduce((acc, element) => {
    acc.push({ ...element, depth })

    if (progressViewState.expandedItems.includes(element.id) && !isEmpty(element.childElements)) {
      acc.push(...flattenElements(element.childElements, depth + 1))
    }

    return acc
  }, [])
}

const progressViewRows = computed(() => flattenElements(progressViewState.elements))

const progressViewColumns = [
  {
    key: TABLE_COLUMNS_KEYS.NAME,
    width: 'auto'
  },

  {
    key: TABLE_COLUMNS_KEYS.OWNER,
    width: 32
  },

  {
    key: TABLE_COLUMNS_KEYS.CHECKIN,
    width: 24
  }
]
//#endregion

//#region Progress module injections
const currentModuleState = computed(() => {
  return {
    currentGranulationId: progressViewState.granulationId
  }
})

provide(currentModuleStateInjectionKey, currentModuleState)

const progressViewHandlers = ref({
  openCheckinModal: payload => emit('open-checkin-modal', payload),
  onElementPeriodThresholdUpdated: () => emit('update-okr-elements'),
  onJiraIssueProgressUpdated: () => emit('update-okr-elements')
})

provide(progressViewStateInjectionKey, progressViewHandlers)
//#endregion

//#region Periods and view point
const { periodsForDisplay, minWidth } = useProgressViewPeriods({
  periods: () => progressViewState.periods,
  granulationId: () => progressViewState.granulationId
})

const { isResetButtonDisabled, resetInitialViewPoint, viewPointLastDatesForNavigation } =
  useViewPointLastDate({
    [ELEMENT_PROGRESS_ENTITY_KEYS.PERIODS]: () => progressViewState.periods,
    shouldStopWatcher: false,
    currentGranulationId: () => progressViewState.granulationId
  })

const { getPeriodGranulationOptions } = usePeriodGranulation({ pluralizeOptions: true })

watch(
  () => progressViewState.granulationId,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue && oldValue) {
      resetInitialViewPoint()
    }
  }
)
//#endregion

//#region Request
const api = new ObjectivesApiHandler()

const homePageState = inject(homePageStateInjectionKey)

const route = useRoute()

const requestPayload = computed(() => {
  const { isTimeRangeSelected } = isTimeRangeSelectedInsteadOfInterval({
    ...props.selectedDates,
    intervals: props.selectedIntervals
  })

  const gradeTypes = homePageState.filterValues.value[NEW_HP_FILTERS_KEYS.GRADE_TYPES]

  const workspaceId = !isNull(props.predefinedWorkspaceId)
    ? props.predefinedWorkspaceId
    : route.params.workspaceId

  return {
    searchType: SEARCH_TYPES.HOMEPAGE_METRICS_PROGRESS_VIEW,
    order: [1],
    childOrder: [1],
    searchString: null,
    dueDateFrom: null,
    levelIds: null,
    typeIds: null,
    workspaceIds: homePageState.filterValues.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS],
    workspaceId,
    gradeTypes: isEmpty(gradeTypes) ? DEFAULT_GRADE_TYPES : gradeTypes,
    groupIds: props.userSelected ? null : [props.selectedUserOrGroup],
    ownerIds: props.userSelected ? [props.selectedUserOrGroup] : null,
    intervalIds: isTimeRangeSelected ? UNSELECTED_INTERVAL : props.selectedIntervals,
    ...getAvailableCustomRangeParams({
      selectedDates: props.selectedDates,
      intervalIds: props.selectedIntervals
    })
  }
})

const getProgressData = async ({ appendElements = false } = {}) => {
  progressViewState.isLoading = true
  progressViewState.isSoftLoading = !appendElements && !isEmpty(progressViewState.elements)

  if (!appendElements) {
    progressViewState.limit = progressViewState.offset || DEFAULT_LIMIT
    progressViewState.offset = 0
  }

  const requestParams = {
    progressParameters: {
      ...progressViewState.requestParameters
    },

    ...requestPayload.value,

    offset: progressViewState.offset,
    limit: progressViewState.limit,
    itemsToOpen: progressViewState.expandedItems
  }

  try {
    const { elements, periods } = await api.getObjectives(requestParams)

    progressViewState.showLoadMoreButton = elements.length === progressViewState.limit
    if (progressViewState.showLoadMoreButton) {
      progressViewState.offset += progressViewState.limit
    }

    progressViewState.elements = appendElements
      ? [...progressViewState.elements, ...elements]
      : elements
    progressViewState.periods = periods || []
    progressViewState.granulationId = progressViewState.requestParameters.granulationId
    progressViewState.columnCount = progressViewState.requestParameters.columnCount
  } catch (error) {
    handleError({ error })
  } finally {
    progressViewState.isLoading = false
    progressViewState.isSoftLoading = false
  }

  emit('empty-change', isNoElements.value)
}

const loadMore = () => {
  getProgressData({
    appendElements: true
  })
}
//#endregion

//#region Dates navigation
const moveForward = () => {
  progressViewState.requestParameters.viewPointLastDate =
    viewPointLastDatesForNavigation.value.forward
  getProgressData()
}

const moveBackward = () => {
  progressViewState.requestParameters.viewPointLastDate =
    viewPointLastDatesForNavigation.value.backward
  getProgressData()
}

const moveToStartpoint = () => {
  progressViewState.requestParameters.viewPointLastDate = null
  getProgressData()
}
//#endregion

//#region Granulation and columns
const persistProgressViewConfig = () => {
  const { granulationId, columnCount } = progressViewState.requestParameters

  store.dispatch('system/updateUserSettings', {
    [USER_SETTINGS_MAPPER[HOMEPAGE_METRICS_WITHOUT_KPI_CARD_CONFIG]]: {
      ...metricsCardConfig.value,
      granulationId,
      columnCount
    }
  })
}

const onGranulationChange = granulationId => {
  progressViewState.requestParameters.viewPointLastDate = null
  progressViewState.requestParameters.granulationId = granulationId
  persistProgressViewConfig()
  getProgressData()
}

const onUpdateColumnCount = columnCount => {
  progressViewState.requestParameters.columnCount = columnCount
  persistProgressViewConfig()
  getProgressData()
}
//#endregion

onMounted(getProgressData)

defineExpose({
  updateElements: () => getProgressData()
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';

$space: 24px;

.pspv-LoadMore {
  --ob-alm-margin: 0 0 0 #{$space};
  min-height: 40px;
  display: flex;
  align-items: center;
}

.pspv-Actions {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px $space;
}

.pspv-Action_Nav {
  display: inline-flex;
  align-items: inherit;
  gap: inherit;
  margin-left: auto;
}

.pspv-Action_ColumnsSelect {
  --pvc-trigger-background: #{$grey-3-next};
  --pvc-counter-size: 32px;
  --pvc-counter-padding-inline: 8px 5px;
}

.pspv-Content {
  overflow: hidden;
  display: grid;
  grid-template-columns: 322px 1fr;

  --cdpm-divider-size: 1px;
  --cdpm-divider-color: #{$grey-2-next};
  --cdpm-row-height: 64px;
  --cdpm-header-height: 35px;
  border-top: var(--cdpm-divider-size) solid var(--cdpm-divider-color);
}

.pspv-Elements {
  --row-min-height: calc(var(--cdpm-row-height) - var(--cdpm-divider-size));
  --head-border-bottom: var(--cdpm-divider-size) solid var(--cdpm-divider-color);
  --head-padding-bottom: 0;
  --head-padding-right: calc(#{$page-right-padding} / 2);
  --head-padding-left: #{$space};
  --row-padding: 0 calc(#{$page-right-padding} / 2) 0 #{$space};
  --head-min-height: var(--cdpm-header-height);
  border-right: var(--cdpm-divider-size) solid var(--cdpm-divider-color);
}

.pspv-HeadNameCell {
  font-size: $fs-12;
  color: $dark-3;
  font-weight: fw('medium');
  line-height: 20px;
}

.pspv-NameCell {
  padding-left: calc(
    (var(--okr-table-row-controls-button-width) + var(--okr-table-row-gap)) *
      var(--ob-pspv-row-depth)
  );
  padding-right: var(--okr-table-row-gap);
  display: flex;
  align-items: center;
  gap: var(--okr-table-row-gap);
}

.pspv-NameCell_Expand {
  flex-shrink: 0;
  width: var(--okr-table-row-controls-button-width);
  height: var(--okr-table-row-controls-button-width);
}

.pspv-ExpandButton {
  transition: transform $transition-fast;

  &-expanded {
    transform: rotate(90deg);
  }
}

.pspv-Grid {
  position: relative;
  overflow-x: auto;
  @include styled-native-scrollbar();

  &-reloading {
    pointer-events: none;
  }
}

.pspv-Grid_Loader {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
}

.pspv-Grid_Content {
  width: 100%;
}
</style>
